/**
 * The catalog editor's pure helpers (plan docs/plans/lms-plan.md 8.1, 6.1,
 * 6.5, Appendix E, 18.11, 18.14; WP-44): the sub-view's paths, the labels,
 * the blank shapes a new slide, section and question start from, the
 * scenario starter, the retrain policy sentences, and the lint-path readers.
 * No React, no I/O.
 */
import { format } from 'date-fns';
import type {
  ChoiceOption,
  CourseCategory,
  CourseContent,
  CourseKind,
  CourseQuestion,
  CourseSection,
  QuestionKind,
  QuizSettings,
  Slide,
  SlideLayout,
} from '../../../content/types';
import type { DraftPatch, EditorQuestion, RetrainPolicy, VersionDetail, VersionStatus } from '../../../api/catalog';
import { lmsTab, lmsTabHref } from '../../config';

// ---------------------------------------------------------------------------
// Paths (the sub-view is mounted at /lms/manage/catalog/*, plan 18.3)
// ---------------------------------------------------------------------------

export const CATALOG_BASE = `${lmsTabHref(lmsTab('manage'))}/catalog`;

export const catalogPaths = {
  list: () => CATALOG_BASE,
  course: (courseId: string) => `${CATALOG_BASE}/courses/${encodeURIComponent(courseId)}`,
  version: (versionId: string) => `${CATALOG_BASE}/versions/${encodeURIComponent(versionId)}`,
  preview: (versionId: string) => `${CATALOG_BASE}/versions/${encodeURIComponent(versionId)}/preview`,
  diff: (versionId: string, otherId: string) =>
    `${CATALOG_BASE}/versions/${encodeURIComponent(versionId)}/diff/${encodeURIComponent(otherId)}`,
};

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

/** 8.1: the catalog lists courses by category, in this order (5.2's list). */
export const CATEGORY_ORDER: CourseCategory[] = ['compliance', 'clinical_skills', 'best_practices', 'sop', 'platform', 'custom'];

export const CATEGORY_LABELS: Record<CourseCategory, string> = {
  compliance: 'Compliance',
  clinical_skills: 'Clinical skills',
  best_practices: 'Best practices (scenarios)',
  sop: 'SOPs',
  platform: 'Platform',
  custom: 'Custom',
};

export const KIND_LABELS: Record<CourseKind, string> = {
  quiz: 'Quiz',
  read_ack: 'Read and acknowledge',
  competency: 'Competency (quiz and sign-off)',
};

export const LAYOUT_LABELS: Record<SlideLayout, string> = {
  title: 'Title',
  bullets: 'Bullets',
  two_column: 'Two columns',
  callout: 'Callout',
  steps: 'Steps',
  table: 'Table',
  figure: 'Figure',
  image: 'Image',
  scenario: 'Scenario',
  knowledge_check: 'Knowledge check',
};

export const STATUS_LABELS: Record<VersionStatus, string> = {
  draft: 'Draft',
  published: 'Published',
  superseded: 'Superseded',
  retired: 'Retired',
};

/** Status pill tone per version status (13: a pill always carries its word). */
export const STATUS_TONES: Record<VersionStatus, 'primary' | 'green' | 'gray' | 'amber'> = {
  draft: 'amber',
  published: 'green',
  superseded: 'gray',
  retired: 'gray',
};

// ---------------------------------------------------------------------------
// The retrain policies (8.1, R11): the label and the consequence, spelled out
// ---------------------------------------------------------------------------

export interface PolicyText {
  label: string;
  sentence: string;
}

export const POLICY_ORDER: RetrainPolicy[] = ['immediate', 'acknowledge', 'at_renewal'];

export const POLICY_TEXT: Record<RetrainPolicy, PolicyText> = {
  immediate: {
    label: 'Everyone retrains now',
    sentence:
      "Every current completion of this course is superseded (certificates stay as history). Everyone who holds it, and everyone with an open assignment, gets an assignment to take this version in full, due in the days you set. Learners see the change summary first.",
  },
  acknowledge: {
    label: 'Acknowledge the change',
    sentence:
      "Every current completion is superseded. Holders read the change summary and the document and sign with their password, with no quiz, and their expiry date stays the same. Anyone without a current completion gets a full assignment instead.",
  },
  at_renewal: {
    label: 'Wait for their renewal',
    sentence:
      "Nothing changes for current holders. Open assignments not pinned to a version use this version when the learner starts, an attempt in progress finishes on its version, and every renewal from now on uses this version.",
  },
};

// ---------------------------------------------------------------------------
// Profiles (18.11's lint profiles; 18.14 caps objectives at 5)
// ---------------------------------------------------------------------------

/** The lint profile a category is held to (the backend's content/lint.js profileFor). */
export function profileFor(category: CourseCategory): 'standard' | 'scenario' | 'sop' {
  if (category === 'best_practices') return 'scenario';
  if (category === 'sop') return 'sop';
  return 'standard';
}

/** 18.14: a course carries at most 5 objectives (the objectives slide is a bullets slide, 5 bullets at most). */
export const MAX_OBJECTIVES = 5;

/** 18.11's scenario profile (6.5), the numbers the scenario starter fills in. */
export const SCENARIO_PROFILE = Object.freeze({
  objectives: [1, 3] as const,
  slides: [2, 5] as const,
  pool: [3, 6] as const,
  takeaways: [2, 4] as const,
  whatToRemember: [2, 4] as const,
  minutes: [3, 6] as const,
  passMarkPct: 100,
});

/** 18.11's scenario profile as quiz settings: drawn in full, pass mark 100, unlimited attempts, feedback after each, no certificate. */
export function scenarioQuizSettings(poolSize: number): QuizSettings {
  return {
    pass_mark_pct: SCENARIO_PROFILE.passMarkPct,
    draw_count: poolSize,
    randomize_questions: true,
    randomize_options: true,
    max_attempts_per_day: null,
    cooldown_minutes: 0,
    feedback_mode: 'after_each',
    require_reading: false,
    issue_certificate: false,
  };
}

/** One line per profile, shown beside the editor so the author knows the numbers the lint holds. */
export function profileHint(category: CourseCategory): string {
  switch (profileFor(category)) {
    case 'scenario':
      return "A scenario (6.5): one section of 2 to 5 slides (the situation, the decision point, the principle, the site rule), 1 to 3 objectives, 2 to 4 takeaways, a pool of 3 to 6 questions all drawn, pass mark 100, unlimited attempts, feedback after each question, no certificate, no expiry, 3 to 6 minutes.";
    case 'sop':
      return "An SOP course (11.2): a title slide, what changed and the SOP itself; at least one source (the SOP file).";
    default:
      return "A course (6.1, 6.2): 3 to 5 objectives, 4 to 12 sections of 3 to 9 slides, a knowledge check in every section, 5 to 8 takeaways, a question pool at least twice the draw, and the closing section \"Putting it into practice at the site\".";
  }
}

// ---------------------------------------------------------------------------
// Blank shapes (Appendix E)
// ---------------------------------------------------------------------------

function option(id: ChoiceOption['id'], text: string, correct: boolean): ChoiceOption {
  return { id, text, correct, feedback: '' };
}

/** The options a question of `kind` starts with (5.2: true_false has exactly two; single exactly one correct). */
export function blankOptions(kind: QuestionKind): ChoiceOption[] {
  if (kind === 'true_false') return [option('a', 'True', true), option('b', 'False', false)];
  return [option('a', '', true), option('b', '', false), option('c', '', false)];
}

/**
 * A blank slide of `layout` with `id`. When `from` is given (a layout change),
 * the heading and speaker notes carry over; everything else starts blank.
 */
export function blankSlide(layout: SlideLayout, id: string, from?: Slide | null): Slide {
  const prior = from as { heading?: string | null; speaker_notes?: string | null } | null | undefined;
  const heading = typeof prior?.heading === 'string' ? prior.heading : '';
  const speaker_notes = prior?.speaker_notes ?? null;
  switch (layout) {
    case 'title':
      return { id, layout, heading, body_md: '', speaker_notes };
    case 'bullets':
      return { id, layout, heading, bullets: [''], speaker_notes };
    case 'two_column':
      return { id, layout, heading, left_md: '', right_md: '', speaker_notes };
    case 'callout':
      return { id, layout, heading, tone: 'rule', body_md: '', speaker_notes };
    case 'steps':
      return { id, layout, heading, steps: [{ title: '', body_md: '' }], speaker_notes };
    case 'table':
      return { id, layout, heading, columns: ['', ''], rows: [['', '']], speaker_notes };
    case 'figure':
      return {
        id,
        layout,
        heading,
        figure: {
          kind: 'flow',
          nodes: [
            { id: 'n1', label: '', note: null },
            { id: 'n2', label: '', note: null },
          ],
          edges: [{ from: 'n1', to: 'n2', label: null }],
        },
        text_equivalent: '',
        speaker_notes,
      };
    case 'image':
      return { id, layout, heading, asset_id: '', alt: '', caption: null, speaker_notes };
    case 'scenario':
      return {
        id,
        layout,
        heading,
        role: '',
        situation_md: '',
        question: '',
        options: [
          { id: 'a', text: '', best: true, feedback: '' },
          { id: 'b', text: '', best: false, feedback: '' },
          { id: 'c', text: '', best: false, feedback: '' },
        ],
        speaker_notes,
      };
    case 'knowledge_check':
    default:
      return {
        id,
        layout: 'knowledge_check',
        heading: heading || null,
        stem: '',
        kind: 'single',
        options: [option('a', '', true), option('b', '', false)],
        explanation: '',
        speaker_notes,
      };
  }
}

/** A new section (Appendix E): empty reading, no slides yet. */
export function blankSection(id: string, title = ''): CourseSection {
  return { id, title, estimated_minutes: 1, why_it_matters: '', slides: [], reading_md: '', what_to_remember: [] };
}

/** A new quiz question tied to a section and the first objective (6.2 rule 1). */
export function blankQuestion(sectionId: string, ordinal: number, kind: QuestionKind = 'single'): EditorQuestion {
  return {
    ordinal,
    section_id: sectionId,
    objective_index: 0,
    kind,
    stem: '',
    options: blankOptions(kind),
    explanation: '',
    difficulty: 'apply',
    source_ref: null,
  };
}

/** A section id not used yet: s1, s2, ... */
export function nextSectionId(content: CourseContent): string {
  const used = new Set((content.sections || []).map((s) => s.id));
  let n = (content.sections || []).length + 1;
  while (used.has(`s${n}`)) n += 1;
  return `s${n}`;
}

/** A slide id not used anywhere in the deck: <section id>-<n>. */
export function nextSlideId(content: CourseContent, sectionId: string): string {
  const used = new Set<string>();
  (content.sections || []).forEach((s) => (s.slides || []).forEach((sl) => used.add(sl.id)));
  const section = (content.sections || []).find((s) => s.id === sectionId);
  let n = (section?.slides || []).length + 1;
  while (used.has(`${sectionId}-${n}`)) n += 1;
  return `${sectionId}-${n}`;
}

/** Questions as the PUT sends them: authored fields only, ordinals renumbered by position. */
export function questionInputs(questions: EditorQuestion[]): CourseQuestion[] {
  return questions.map((q, i) => {
    const { id: _id, ...rest } = q;
    return { ...rest, ordinal: i + 1 };
  });
}

/**
 * The scenario starter (6.5; the numbers of 18.11's scenario profile). The
 * server's template for a best_practices course already holds the section and
 * slide shapes (the situation with its decision, the principle, the site
 * rule); this adds what it leaves empty: one objective, two takeaways, two
 * "what to remember" lines and a pool of three questions drawn in full, so the
 * author fills in blanks instead of counting.
 */
export function scenarioStarter(detail: VersionDetail): DraftPatch {
  const content: CourseContent = JSON.parse(JSON.stringify(detail.content || {}));
  const sections = Array.isArray(content.sections) ? content.sections : [];
  if (sections.length === 0) sections.push(blankSection('s1', detail.course.title));
  const first = sections[0];
  const fill = (list: string[] | undefined, min: number) => {
    const out = Array.isArray(list) ? [...list] : [];
    while (out.length < min) out.push('');
    return out;
  };
  content.sections = sections;
  content.learning_objectives = fill(content.learning_objectives, SCENARIO_PROFILE.objectives[0]);
  content.key_takeaways = fill(content.key_takeaways, SCENARIO_PROFILE.takeaways[0]);
  first.what_to_remember = fill(first.what_to_remember, SCENARIO_PROFILE.whatToRemember[0]);
  const existing = Array.isArray(detail.questions) ? detail.questions : [];
  const questions: EditorQuestion[] = [...existing];
  while (questions.length < SCENARIO_PROFILE.pool[0]) questions.push(blankQuestion(first.id, questions.length + 1));
  return {
    content,
    questions: questionInputs(questions),
    quiz_settings: scenarioQuizSettings(questions.length),
  };
}

// ---------------------------------------------------------------------------
// Lint paths ($.content.sections[1].slides[0].heading, 18.11)
// ---------------------------------------------------------------------------

/** The section index a lint path points into, or null. */
export function lintSectionIndex(path: string): number | null {
  const m = /\bsections\[(\d+)\]/.exec(path || '');
  return m ? Number(m[1]) : null;
}

/** The slide index a lint path points into, or null. */
export function lintSlideIndex(path: string): number | null {
  const m = /\bslides\[(\d+)\]/.exec(path || '');
  return m ? Number(m[1]) : null;
}

/** The question index a lint path points into (quiz.questions[n]), or null. */
export function lintQuestionIndex(path: string): number | null {
  const m = /\bquestions\[(\d+)\]/.exec(path || '');
  return m ? Number(m[1]) : null;
}

/** 8.1: the change summary is asked for in the publish dialog, so its lint rule does not block opening it. */
export const PUBLISH_DIALOG_RULES = new Set(['change_summary']);

// ---------------------------------------------------------------------------
// Small immutable helpers
// ---------------------------------------------------------------------------

export function replaceAt<T>(list: T[], index: number, value: T): T[] {
  return list.map((item, i) => (i === index ? value : item));
}

export function removeAt<T>(list: T[], index: number): T[] {
  return list.filter((_, i) => i !== index);
}

export function moveItem<T>(list: T[], from: number, to: number): T[] {
  if (to < 0 || to >= list.length || from === to) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

/** A date as the admin reads it (plain, locale-neutral), or an em dash. */
export function formatDate(value: string | null | undefined): string {
  if (!value) return '\u2014';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '\u2014';
  return format(d, 'd MMM yyyy');
}

/** "12 months", "No expiry" (5.2: NULL validity means a completion never expires). */
export function validityLabel(months: number | null | undefined): string {
  if (months === null || months === undefined) return 'No expiry';
  return months === 1 ? '1 month' : `${months} months`;
}
